import { useEffect, useState } from 'react';
import { Tabs, Table, Button, DatePicker, Space, App, Spin, Tag, Progress, Empty } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid,
  LineChart, Line,
} from 'recharts';
import { Download, FileSpreadsheet, TrendingUp, AlertTriangle, Users, FileSignature } from 'lucide-react';
import dayjs, { Dayjs } from 'dayjs';
import { PageHeader, Card } from '@/components/common/PageHeader';
import {
  reportsService,
  type OverdueByDeptRow, type UserPerformanceRow,
  type MonthlyProgressRow, type PendingSignatureRow,
} from '@/services/reportsService';
import { TaskListPopupModal, type TaskListPopupParams } from '@/components/reports/TaskListPopupModal';

type ReportTab = 'overdue-by-dept' | 'user-performance' | 'monthly-progress' | 'pending-signatures';

export function ReportsPage() {
  const [tab, setTab] = useState<ReportTab>('overdue-by-dept');
  const [fromDate, setFromDate] = useState<Dayjs | null>(dayjs().subtract(3, 'month'));
  const [toDate, setToDate] = useState<Dayjs | null>(dayjs());

  return (
    <>
      <PageHeader
        title="Báo cáo & Thống kê"
        subtitle="Tổng hợp hiệu suất, tiến độ và luồng ký toàn viện"
        extra={
          <Space>
            <DatePicker
              value={fromDate} onChange={setFromDate}
              placeholder="Từ ngày" format="DD/MM/YYYY" style={{ width: 150 }}
            />
            <span style={{ color: '#8a95a5' }}>—</span>
            <DatePicker
              value={toDate} onChange={setToDate}
              placeholder="Đến ngày" format="DD/MM/YYYY" style={{ width: 150 }}
            />
          </Space>
        }
      />

      <Card padding={0}>
        <Tabs
          activeKey={tab}
          onChange={(k) => setTab(k as ReportTab)}
          style={{ padding: '0 20px', borderBottom: '1px solid #e1e5eb' }}
          items={[
            {
              key: 'overdue-by-dept',
              label: <span><AlertTriangle size={12} style={{ verticalAlign: -1, marginRight: 6 }} />Quá hạn theo khoa</span>,
            },
            {
              key: 'user-performance',
              label: <span><Users size={12} style={{ verticalAlign: -1, marginRight: 6 }} />Hiệu suất user</span>,
            },
            {
              key: 'monthly-progress',
              label: <span><TrendingUp size={12} style={{ verticalAlign: -1, marginRight: 6 }} />Tiến độ theo tháng</span>,
            },
            {
              key: 'pending-signatures',
              label: <span><FileSignature size={12} style={{ verticalAlign: -1, marginRight: 6 }} />Ký chờ tồn đọng</span>,
            },
          ]}
        />

        <div style={{ padding: 20 }}>
          {tab === 'overdue-by-dept' && <OverdueByDeptReport fromDate={fromDate} toDate={toDate} />}
          {tab === 'user-performance' && <UserPerformanceReport fromDate={fromDate} toDate={toDate} />}
          {tab === 'monthly-progress' && <MonthlyProgressReport />}
          {tab === 'pending-signatures' && <PendingSignaturesReport />}
        </div>
      </Card>
    </>
  );
}

// ============================================================
// Helper: Export button
// ============================================================
function ExportBar({ reportType, params }: { reportType: string; params?: any }) {
  const { message } = App.useApp();
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      await reportsService.exportExcel(reportType, params);
      message.success('Đã xuất Excel');
    } catch (e: any) {
      message.error(e.response?.data?.message ?? e.message);
    } finally { setExporting(false); }
  };

  const handlePrintPdf = () => window.print();

  return (
    <Space>
      <Button icon={<FileSpreadsheet size={14} />} loading={exporting} onClick={handleExport}>
        Xuất Excel
      </Button>
      <Button icon={<Download size={14} />} onClick={handlePrintPdf}>
        Xuất PDF (In/Save as PDF)
      </Button>
    </Space>
  );
}

// ============================================================
// 1. Task quá hạn theo khoa
// ============================================================
function OverdueByDeptReport({ fromDate, toDate }: { fromDate: Dayjs | null; toDate: Dayjs | null }) {
  const { message } = App.useApp();
  const [data, setData] = useState<OverdueByDeptRow[]>([]);
  const [loading, setLoading] = useState(false);
  // V1.0.35: Popup state — drill-down từ chart/table
  const [popupParams, setPopupParams] = useState<TaskListPopupParams | null>(null);

  const params = {
    fromDate: fromDate?.format('YYYY-MM-DD'),
    toDate: toDate?.format('YYYY-MM-DD'),
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const rows = await reportsService.overdueByDept(params.fromDate, params.toDate);
        setData(rows);
      } catch (e: any) {
        message.error(e.response?.data?.message ?? e.message);
      } finally { setLoading(false); }
    })();
  }, [fromDate, toDate]);

  // V1.0.35: Helper mở popup
  const openPopup = (row: OverdueByDeptRow, bucket: 'all' | 'overdue' | 'inProgress' | 'completed') => {
    const bucketLabel: Record<typeof bucket, string> = {
      all: 'Tất cả task',
      overdue: 'Task quá hạn',
      inProgress: 'Task đang làm',
      completed: 'Task hoàn thành',
    };
    const bucketColor: Record<typeof bucket, string> = {
      all: '#1976D2',
      overdue: '#ff4d4f',
      inProgress: '#6699CC',
      completed: '#52c41a',
    };
    setPopupParams({
      visible: true,
      title: `${bucketLabel[bucket]} — ${row.deptName}`,
      color: bucketColor[bucket],
      departmentId: row.deptId,
      statusBucket: bucket,
      fromDate: params.fromDate,
      toDate: params.toDate,
    });
  };

  const columns: ColumnsType<OverdueByDeptRow> = [
    { title: 'Khoa/Phòng', dataIndex: 'deptName',
      render: (n: string, r: OverdueByDeptRow) => (
        <div>
          <div style={{ fontWeight: 500 }}>{n}</div>
          <div className="mono" style={{ fontSize: 10, color: '#8a95a5' }}>{r.deptCode}</div>
        </div>
      ),
    },
    {
      title: 'Tổng', dataIndex: 'total', align: 'right' as const, width: 80,
      render: (v: number, r: OverdueByDeptRow) => v > 0 ? (
        <a
          onClick={() => openPopup(r, 'all')}
          style={{ fontWeight: 600 }}
          title="Click để xem danh sách task"
        >
          {v}
        </a>
      ) : <span style={{ color: '#bdc4cd' }}>{v}</span>,
    },
    {
      title: 'Quá hạn', dataIndex: 'overdue', align: 'right' as const, width: 100,
      render: (v: number, r: OverdueByDeptRow) => v > 0 ? (
        <a
          onClick={() => openPopup(r, 'overdue')}
          style={{ color: '#ff4d4f', fontWeight: 500 }}
          title="Click để xem danh sách task quá hạn"
        >
          {v}
        </a>
      ) : <span style={{ color: '#52c41a', fontWeight: 500 }}>{v}</span>,
    },
    {
      title: 'Đang làm', dataIndex: 'inProgress', align: 'right' as const, width: 100,
      render: (v: number, r: OverdueByDeptRow) => v > 0 ? (
        <a
          onClick={() => openPopup(r, 'inProgress')}
          style={{ color: '#6699CC', fontWeight: 500 }}
          title="Click để xem danh sách task đang làm"
        >
          {v}
        </a>
      ) : <span style={{ color: '#bdc4cd' }}>{v}</span>,
    },
    {
      title: 'Hoàn thành', dataIndex: 'completed', align: 'right' as const, width: 110,
      render: (v: number, r: OverdueByDeptRow) => v > 0 ? (
        <a
          onClick={() => openPopup(r, 'completed')}
          style={{ color: '#52c41a', fontWeight: 500 }}
          title="Click để xem danh sách task hoàn thành"
        >
          {v}
        </a>
      ) : <span style={{ color: '#bdc4cd' }}>{v}</span>,
    },
    {
      title: 'Tỷ lệ quá hạn', width: 180,
      render: (_: any, r: OverdueByDeptRow) => {
        const rate = r.total > 0 ? Math.round((r.overdue / r.total) * 100) : 0;
        return (
          <div>
            <Progress percent={rate} size="small" showInfo={false}
              strokeColor={rate > 30 ? '#ff4d4f' : rate > 15 ? '#faad14' : '#52c41a'} />
            <div style={{ fontSize: 11, color: '#5b6875', marginTop: 2 }}>{rate}%</div>
          </div>
        );
      },
    },
  ];

  // V1.0.35: Click vào cột chart → mở popup
  const handleChartClick = (e: any) => {
    if (e && e.activePayload && e.activePayload.length > 0) {
      const row = e.activePayload[0].payload as OverdueByDeptRow;
      if (row && row.deptId) {
        openPopup(row, 'all');
      }
    }
  };

  return (
    <Spin spinning={loading}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <div style={{ fontWeight: 600 }}>Task quá hạn theo khoa/phòng</div>
          <div style={{ fontSize: 11, color: '#8a95a5' }}>
            Thống kê task từ {fromDate?.format('DD/MM/YYYY')} đến {toDate?.format('DD/MM/YYYY')}
            {' · '}
            <span style={{ color: '#1976D2' }}>💡 Click vào cột chart hoặc số trong bảng để xem chi tiết task</span>
          </div>
        </div>
        <ExportBar reportType="overdue-by-dept" params={params} />
      </div>

      {data.length > 0 ? (
        <>
          <div style={{ height: 300, marginBottom: 20 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={data}
                margin={{ top: 10, right: 20, left: 0, bottom: 40 }}
                onClick={handleChartClick}
                style={{ cursor: 'pointer' }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e1e5eb" />
                <XAxis dataKey="deptCode" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" height={60} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="completed" stackId="a" fill="#52c41a" name="Hoàn thành" />
                <Bar dataKey="inProgress" stackId="a" fill="#6699CC" name="Đang làm" />
                <Bar dataKey="overdue" stackId="a" fill="#ff4d4f" name="Quá hạn" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <Table columns={columns} dataSource={data} rowKey="deptCode" size="middle" pagination={false} />
        </>
      ) : (
        <Empty description="Không có dữ liệu trong khoảng thời gian này" />
      )}

      {/* V1.0.35: Drill-down popup */}
      <TaskListPopupModal
        params={popupParams}
        onClose={() => setPopupParams(null)}
      />
    </Spin>
  );
}

// ============================================================
// 2. Hiệu suất user
// ============================================================
function UserPerformanceReport({ fromDate, toDate }: { fromDate: Dayjs | null; toDate: Dayjs | null }) {
  const { message } = App.useApp();
  const [data, setData] = useState<UserPerformanceRow[]>([]);
  const [loading, setLoading] = useState(false);

  const params = {
    fromDate: fromDate?.format('YYYY-MM-DD'),
    toDate: toDate?.format('YYYY-MM-DD'),
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const rows = await reportsService.userPerformance(params.fromDate, params.toDate);
        setData(rows);
      } catch (e: any) {
        message.error(e.response?.data?.message ?? e.message);
      } finally { setLoading(false); }
    })();
  }, [fromDate, toDate]);

  const columns: ColumnsType<UserPerformanceRow> = [
    {
      title: 'Người dùng',
      render: (_: any, r: UserPerformanceRow) => (
        <div>
          <div style={{ fontWeight: 500 }}>{r.fullName}</div>
          <div style={{ fontSize: 11, color: '#8a95a5' }}>
            <span className="mono">{r.username}</span>
            {r.deptName ? ` · ${r.deptName}` : ''}
          </div>
        </div>
      ),
    },
    { title: 'Tổng task', dataIndex: 'total', align: 'right' as const, width: 100 },
    { title: 'Hoàn thành', dataIndex: 'completed', align: 'right' as const, width: 110,
      render: (v: number) => <span style={{ color: '#52c41a' }}>{v}</span> },
    { title: 'Đúng hạn', dataIndex: 'onTime', align: 'right' as const, width: 100 },
    { title: 'Quá hạn', dataIndex: 'overdue', align: 'right' as const, width: 100,
      render: (v: number) => <span style={{ color: v > 0 ? '#ff4d4f' : '#8a95a5' }}>{v}</span> },
    {
      title: 'Tỷ lệ HT', dataIndex: 'completionRate', width: 160,
      render: (v: number) => (
        <div>
          <Progress percent={Number(v)} size="small" showInfo={false}
            strokeColor={v >= 90 ? '#52c41a' : v >= 70 ? '#6699CC' : v >= 50 ? '#faad14' : '#ff4d4f'} />
          <div style={{ fontSize: 11, color: '#5b6875', marginTop: 2 }}>{Number(v).toFixed(1)}%</div>
        </div>
      ),
    },
    {
      title: 'Tiến độ TB', dataIndex: 'avgProgress', align: 'right' as const, width: 110,
      render: (v: number) => <span>{Number(v).toFixed(1)}%</span>,
    },
  ];

  return (
    <Spin spinning={loading}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <div style={{ fontWeight: 600 }}>Hiệu suất người dùng</div>
          <div style={{ fontSize: 11, color: '#8a95a5' }}>
            Top 50 user có task trong khoảng thời gian
          </div>
        </div>
        <ExportBar reportType="user-performance" params={params} />
      </div>

      {data.length > 0 ? (
        <Table columns={columns} dataSource={data} rowKey="userId" size="middle"
          pagination={{ pageSize: 20 }} scroll={{ x: 900 }} />
      ) : (
        <Empty description="Không có dữ liệu" />
      )}
    </Spin>
  );
}

// ============================================================
// 3. Tiến độ theo tháng
// ============================================================
function MonthlyProgressReport() {
  const { message } = App.useApp();
  const [data, setData] = useState<MonthlyProgressRow[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const rows = await reportsService.monthlyProgress(12);
        setData(rows);
      } catch (e: any) {
        message.error(e.response?.data?.message ?? e.message);
      } finally { setLoading(false); }
    })();
  }, []);

  const columns: ColumnsType<MonthlyProgressRow> = [
    { title: 'Tháng', dataIndex: 'month', width: 120 },
    { title: 'Tạo mới', dataIndex: 'created', align: 'right' as const },
    { title: 'Hoàn thành', dataIndex: 'completed', align: 'right' as const,
      render: (v: number) => <span style={{ color: '#52c41a' }}>{v}</span> },
    { title: 'Quá hạn', dataIndex: 'overdue', align: 'right' as const,
      render: (v: number) => <span style={{ color: v > 0 ? '#ff4d4f' : '#8a95a5' }}>{v}</span> },
  ];

  return (
    <Spin spinning={loading}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <div style={{ fontWeight: 600 }}>Tiến độ theo tháng (12 tháng gần nhất)</div>
        </div>
        <ExportBar reportType="monthly-progress" />
      </div>

      {data.length > 0 ? (
        <>
          <div style={{ height: 340, marginBottom: 20 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e1e5eb" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="created" stroke="#6699CC" name="Tạo mới" strokeWidth={2} />
                <Line type="monotone" dataKey="completed" stroke="#52c41a" name="Hoàn thành" strokeWidth={2} />
                <Line type="monotone" dataKey="overdue" stroke="#ff4d4f" name="Quá hạn" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <Table columns={columns} dataSource={data} rowKey="month" size="middle" pagination={false} />
        </>
      ) : (
        <Empty description="Không có dữ liệu" />
      )}
    </Spin>
  );
}

// ============================================================
// 4. Ký chờ tồn đọng
// ============================================================
function PendingSignaturesReport() {
  const { message } = App.useApp();
  const [data, setData] = useState<PendingSignatureRow[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const rows = await reportsService.pendingSignatures();
        setData(rows);
      } catch (e: any) {
        message.error(e.response?.data?.message ?? e.message);
      } finally { setLoading(false); }
    })();
  }, []);

  const columns: ColumnsType<PendingSignatureRow> = [
    {
      title: 'Tài liệu',
      render: (_: any, r: PendingSignatureRow) => (
        <div>
          <div style={{ fontWeight: 500 }}>{r.documentName}</div>
          <div className="mono" style={{ fontSize: 10, color: '#8a95a5' }}>{r.documentId}</div>
        </div>
      ),
    },
    { title: 'Người gửi', dataIndex: 'creatorName', width: 160 },
    {
      title: 'Tiến độ ký', width: 200,
      render: (_: any, r: PendingSignatureRow) => {
        const percent = r.totalSigners > 0 ? Math.round((r.signed / r.totalSigners) * 100) : 0;
        return (
          <div>
            <Progress percent={percent} size="small" showInfo={false} />
            <div style={{ fontSize: 11, color: '#5b6875', marginTop: 2 }}>
              {r.signed}/{r.totalSigners} người đã ký
            </div>
          </div>
        );
      },
    },
    {
      title: 'Số ngày chờ', dataIndex: 'daysElapsed', align: 'right' as const, width: 130,
      render: (d: number) => (
        <Tag color={d > 7 ? 'error' : d > 3 ? 'warning' : 'default'}>
          {d} ngày
        </Tag>
      ),
    },
    {
      title: 'Ngày tạo', dataIndex: 'createdAt', width: 160,
      render: (t: string) => <span className="mono" style={{ fontSize: 11 }}>{dayjs(t).format('DD/MM/YYYY HH:mm')}</span>,
    },
  ];

  return (
    <Spin spinning={loading}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <div style={{ fontWeight: 600 }}>Yêu cầu ký đang tồn đọng ({data.length})</div>
          <div style={{ fontSize: 11, color: '#8a95a5' }}>
            Danh sách tài liệu chờ ký, sắp xếp theo thứ tự cũ nhất
          </div>
        </div>
        <ExportBar reportType="pending-signatures" />
      </div>

      {data.length > 0 ? (
        <Table columns={columns} dataSource={data} rowKey="requestId" size="middle"
          pagination={{ pageSize: 20 }} scroll={{ x: 800 }} />
      ) : (
        <Empty description="Không có yêu cầu ký tồn đọng" />
      )}
    </Spin>
  );
}
